import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AuthTokens, UserRole } from '@/types';
import { isTokenExpired } from '@/utils';

export interface UserInfoData {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  country: string;
  username: string | null;
  status: string;
  isVerified: boolean;
  profileImage: string | null;
  wallet: string | null;
}

// -----for locl storage save----------
export interface UserLocalData {
  firstname: string;
  lastname: string;
  profileImage: string | null;
  wallet: string | null;
}

interface AuthState {
  type: 'admin' | null;
  user: UserInfoData | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// ✅ Initial State Loader
const getInitialState = (): AuthState => {
  if (typeof window === 'undefined') {
    return {
      type: null,
      user: null,
      accessToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    };
  }

  const storedToken = localStorage.getItem('accessToken');
  const storedUser = localStorage.getItem('user');
  const isTokenValid = storedToken && !isTokenExpired(storedToken);

  return {
    type: null,
    user: storedUser && isTokenValid ? JSON.parse(storedUser) : null,
    accessToken: isTokenValid ? storedToken : null,
    isAuthenticated: Boolean(isTokenValid),
    isLoading: false,
    error: null,
  };
};

const initialState: AuthState = getInitialState();

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // -----------------------------
    // Loading + Errors
    // -----------------------------
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isLoading = false;
    },

    // -----------------------------
    // Login (ONLY store token)
    // -----------------------------
    login: (state, action: PayloadAction<{ type: 'admin'; accessToken: string }>) => {
      const { type, accessToken } = action.payload;
      state.type = type;
      state.user = null;
      state.accessToken = accessToken;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.error = null;
      // Persist to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('type', type);
      }
    },

    // -----------------------------
    // LOGOUT
    // -----------------------------
    logout: (state) => {
      state.user = null;
      state.accessToken = null;
      state.isAuthenticated = false;
      state.error = null;

      if (typeof window !== 'undefined') {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
      }
    },

    // -----------------------------
    // STORE CLEAN USER DATA
    // -----------------------------
    setUser: (state, action: PayloadAction<any>) => {
      const apiUser = action.payload;

      const filteredUser: UserInfoData = {
        id: apiUser.id,
        firstname: apiUser.firstname,
        lastname: apiUser.lastname,
        email: apiUser.email,
        country: apiUser.country,
        username: apiUser.username,
        status: apiUser.status,
        isVerified: apiUser.isVerified,
        profileImage: apiUser.profileImage,
        wallet: apiUser.wallet.amount,
      };
      state.user = filteredUser;

      // ------store in sessionStorage------------
      const localUser: UserLocalData = {
        firstname: apiUser.firstname,
        lastname: apiUser.lastname,
        profileImage: apiUser.profileImage,
        wallet: apiUser.wallet.amount,
      };
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('user', JSON.stringify(localUser));
      }
    },

    // -----------------------------
    // REFRESH TOKEN / SAVE NEW TOKEN
    // -----------------------------
    setTokens: (state, action: PayloadAction<AuthTokens>) => {
      state.accessToken = action.payload.accessToken;
      state.isAuthenticated = true;

      if (typeof window !== 'undefined') {
        localStorage.setItem('accessToken', action.payload.accessToken);
      }
    },

    // -----------------------------
    // CLEAR AUTH COMPLETELY
    // -----------------------------
    clearAuth: (state) => {
      state.user = null;
      state.accessToken = null;
      state.isAuthenticated = false;
      state.error = null;

      if (typeof window !== 'undefined') {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
      }
    },
  },
});

export const { setLoading, setError, login, logout, setUser, setTokens, clearAuth } =
  authSlice.actions;

export default authSlice.reducer;
